import { DrawingTypes } from '@/types';
import { ShapeTypes } from '@/types/shapes';
import { RGBColor } from '@/types/colors';

export const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'pt', 'tr', 'fr', 'ru'];

export enum PROFILE_ROUTES {
  general = 'general',
  cards = 'cards',
  subscriptions = 'subscriptions',
  payments = 'payments',
}

export const ROUTES = {
  home: (lang: string) => `/${lang}`,
  restorePassword: (lang: string) => `/${lang}/restore-password/:code`,
  edit: (lang: string, documentId: string) => `/${lang}/edit/${documentId}`,
  editNew: (lang: string) => `/${lang}/edit/new`,
  results: (lang: string, documentId: string) => `/${lang}/results/${documentId}`,
  myFiles: (lang: string) => `/${lang}/my-documents`,
  pricing: (lang: string) => `/${lang}/pricing`,
  refund: (lang: string) => `/${lang}/refund-policy`,
  policy: (lang: string) => `/${lang}/privacy-policy`,
  terms: (lang: string) => `/${lang}/terms-of-service`,
  gdpr: (lang: string) => `/${lang}/gdpr`,
  workInProgress: (lang: string) => `/${lang}/work-in-progress`,
  subscribed: (lang: string) => `/${lang}/subscribed`,
  convertToWord: (lang: string) => `/${lang}/convert/pdf-to-word`,
  profile: (lang: string, subRoute: PROFILE_ROUTES | '*') => `/${lang}/profile/${subRoute}`,
  /** Dev-only design-system specimen page. Not routed in production builds. */
  designSystem: (lang: string) => `/${lang}/design-system`,
};

/* -------------------------------------- Signatures -------------------------------------- */
export const SIGNATURE_COLORS = ['#001F3F', '#083358', '#0D63A5', '#FFD717', '#FFFFFF'];

/**
 * Bitmap size of the Draw tab's canvas, in device-independent pixels.
 *
 * 5:2 rather than the old 5:3: the frame fills the dialog body now, and at that width a
 * taller box is mostly empty air above and below the signature. 640 is within a few percent
 * of the 624px the frame actually occupies in a `sm:max-w-2xl` dialog, so the bitmap maps
 * about 1:1 to the display — which is what keeps strokes crisp on export and keeps them the
 * same on-screen weight as before, since `drawStrokeWithPenEffect` sizes them in bitmap px.
 */
export const SIGNATURE_DRAWING_AREA_WIDTH = 640;
export const SIGNATURE_DRAWING_AREA_HEIGHT = 256;

/**
 * Em size the Type tab rasterises at — deliberately far larger than the ~34px the preview
 * cards display, and matched to the drawing canvas's height so a typed signature carries
 * about as much detail as a drawn one.
 *
 * This does not control how big the signature lands on the page: `computeInsertedImageSize`
 * caps both dimensions at `MAX_INSERTED_IMAGE_SIZE`, so a longer name is scaled down to the
 * same placed width either way. What the extra pixels buy is everything downstream of that
 * — resizing the placed signature, and export, where the old 50px raster was close to 1:1
 * with its placed size and had nothing in reserve.
 */
export const SIGNATURE_FONT_SIZE = 200;

/**
 * The Type tab's handwriting faces, loaded from Google Fonts in `index.html`.
 *
 * Every one of these carries the `cyrillic` and `latin-ext` subsets — checked against the
 * live API, not assumed. That rules out most of Google's script faces: of the five this list
 * used to hold (Over The Rainbow, Stalemate, Caveat, Cedarville Cursive, Dancing Script) only
 * Caveat had Cyrillic at all, and Cedarville Cursive was latin-only, so it could not even set
 * a Turkish name. RU and TR are shipping locales; a signature is a person's own name, so a
 * face that cannot spell it is not an option.
 *
 * Marck Script and Comforter carry `cyrillic` but not `cyrillic-ext`, so the Kazakh-only
 * letters (Әә Ғғ Ққ Ңң Өө Ұұ Үү Һһ Іі, design system §8) fall back in those two. There is no
 * KZ locale today; if one lands, they are the two to re-check.
 *
 * Keep this in step with the `family=` list in `index.html`.
 */
export const SIGNATURE_TYPING_FONTS = ['Caveat', 'Bad Script', 'Marck Script', 'Great Vibes', 'Comforter', 'Pacifico'];

/* -------------------------------------- Annotations -------------------------------------- */

// Palette offered by the Draw picker and the post-draw toolbar. Three inks, the highlighter
// yellow, and the destructive red: marking a document up is the reason this tool exists, and
// red was the one colour a reader expects for it.
export const ANNOTATION_DRAWING_COLORS = ['#001F3F', '#083358', '#0D63A5', '#FFD717', '#A11B1B'];

export const ANNOTATION_LINE_WIDTHS = {
  [DrawingTypes.Draw]: 7,
  [DrawingTypes.Highlight]: 20,
};

/** Selectable stroke widths (px) for annotations — shared by the Draw picker and the post-draw toolbar. */
export const ANNOTATION_MIN_WIDTH = 1;
export const ANNOTATION_MAX_WIDTH = 32;
export const ANNOTATION_WIDTH_OPTIONS = Array.from({ length: ANNOTATION_MAX_WIDTH }, (_, i) => ({
  label: String(i + 1),
  value: i + 1,
}));

export const DUPLICATE_SHAPE_OFFSET = 10;

/* -------------------------------------- Shapes -------------------------------------- */

// Palette offered by the Shapes picker (shared with annotations for visual consistency, which
// a test in constants.test.ts holds them to).
export const SHAPE_DRAWING_COLORS = ['#001F3F', '#083358', '#0D63A5', '#FFD717', '#A11B1B'];

// Selectable border/line width (px) range for the Shapes picker slider.
export const SHAPE_MIN_WIDTH = 1;
export const SHAPE_MAX_WIDTH = 32;

// Per-shape default border/line width. Outline shapes are thinner; lines/arrows read heavier.
// Matches the fallbacks applied in shapesReducer's ADD_SHAPE.
export const SHAPE_BORDER_WIDTHS: Record<ShapeTypes, number> = {
  [ShapeTypes.Ellipse]: 2,
  [ShapeTypes.Rectangle]: 2,
  [ShapeTypes.Arrow]: 4,
  [ShapeTypes.Line]: 4,
};

/* -------------------------------------- Document Editor -------------------------------------- */

// TODO: ??? figure out why 2 - isn't it supposed to be calculated ("e.g. "fit page" scale on the start)?
export const DEFAULT_SCALE = 2;

// TODO: ??? maybe determine the scale dynamically - it can depend on the document and screen dimensions
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 4;

export const RESIZE_HANDLE_POSITIONS = {
  Rectangle: ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'],
  Textbox: [
    'topLeft',
    'topRight',
    'bottomLeft',
    'bottomRight',
    'topCenter',
    'bottomCenter',
    'leftCenter',
    'rightCenter',
  ],
} as const;

export const RESIZE_HANDLE_STYLES = {
  Rectangle: {
    cursor: {
      topLeft: 'nw-resize',
      topRight: 'ne-resize',
      bottomLeft: 'sw-resize',
      bottomRight: 'se-resize',
    } as Record<string, string>,
  },
  Textbox: {
    cursor: {
      topLeft: 'nw-resize',
      topRight: 'ne-resize',
      bottomLeft: 'sw-resize',
      bottomRight: 'se-resize',
      topCenter: 'n-resize',
      bottomCenter: 's-resize',
      leftCenter: 'w-resize',
      rightCenter: 'e-resize',
    } as Record<string, string>,
  },
};

// Rendered size of a resize handle, in CSS px. Layers that position their handles from JS
// (Ellipse, ResizableImage, and the textbox handles in functions/resizeHandleHelpers.ts)
// subtract half of this to center a handle on the point it grabs, so it must equal
// `$handle-size` in components/PdfEditor/components/Layers/Common/selection.scss, which is
// what actually sizes the element. selection.test.ts fails if the two drift apart.
export const SHAPE_HANDLE_SIZE = 10;

// How far outside an element's box the selection ring's stroke runs: the ring is inset -4px
// and its 2px border draws inwards, putting the centerline 3px out. Handles center on that
// line, so every layer measures its handles from here rather than from the box edge.
// Mirrored by `$ring-stroke-center` in selection.scss, and guarded by selection.test.ts.
export const SELECTION_RING_STROKE_OFFSET = 3;

// Distance from an element's box edge to a handle's top/left corner, which is the number a
// layer actually writes into `top`/`left`. Mirrors `$handle-inset` in selection.scss.
export const SELECTION_HANDLE_INSET = SELECTION_RING_STROKE_OFFSET + SHAPE_HANDLE_SIZE / 2;

export const MAX_UNDO_STACK_SIZE = 15;

export const WHITEOUT_TEXT_THRESHOLD = 0.5;

// Palette offered by the erase tool when auto-pick color is disabled. White first (the common case).
export const WHITEOUT_COLORS = ['#FFFFFF', '#000000', '#001F3F', '#0D63A5', '#FFD717'];

// Palette offered for text and form-field colour in the properties panel: ink, muted, the
// destructive red, the accent teal and the app's blue, per the design system's colour roles.
// The blue is the one a reader reaches for after black: headings, filled-in form values and
// anything meant to read as added rather than original. These are document content the user
// is choosing, not chrome, so they live here beside the other palettes rather than as
// literals in a component.
export const TEXT_ELEMENT_COLORS = ['#0B0B0C', '#6B7076', '#A11B1B', '#0E8F94', '#0D63A5'];

// localStorage flag: once set, the erase-tool warning dialog is never shown again.
export const WHITEOUT_WARNING_DISMISSED_KEY = 'whiteoutWarningDismissed';

export const SHAPES_DRAWING_POINTS_MIN_DISTANCE = 10;

export const RGB_COLORS: Record<string, RGBColor> = {
  BLACK: { r: 0, g: 0, b: 0, a: 1 },
  GRAY: { r: 128, g: 128, b: 128, a: 1 },
  RED: { r: 255, g: 0, b: 0, a: 1 },
  TRANSPARENT: { r: 0, g: 0, b: 0, a: 0 },
};

export const COORDINATES_PIXELS_EPSILON = 0.5;

export const PDF_TO_WORD_MAX_TOTAL_FILE_SIZE = 50;
